import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

const euro = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

function errorMessage(data) {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.error === "string") return data.error;
  return "Operazione non riuscita.";
}

export default function B2BOrderPromotion({
  ordine,
  onUpdated,
  disabled = false,
}) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const orderId = ordine?.id;
  const activeId = ordine?.b2b_promotion_id;
  const activeTitle = ordine?.b2b_promotion_title;
  const activeStatus = ordine?.b2b_promotion_status;

  const editable =
    ordine?.status === "DRAFT" &&
    !ordine?.b2b_signed_at &&
    !ordine?.is_paid &&
    !disabled;

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const response = await authFetch("/api/b2b/promotions/");
        const data = await response.json().catch(() => ({}));

        if (!response.ok) throw new Error(errorMessage(data));

        if (active) {
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data.results)
              ? data.results
              : [];

          setPromotions(
            list.filter((promotion) =>
              ["PERCENT", "FIXED", "GIFT"].includes(
                promotion.benefit_type
              )
            )
          );
        }
      } catch (err) {
        if (active) setError(err.message || "Promozioni non disponibili.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  async function perform(action, promotionId = null) {
    if (!editable || busy || !orderId) return;

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const endpoint =
        `/api/ordini/${encodeURIComponent(orderId)}/promozione/` +
        (action === "apply" ? "applica/" : "rimuovi/");

      const response = await authFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          action === "apply"
            ? { promotion_id: Number(promotionId) }
            : {}
        ),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(errorMessage(data));

      if (typeof onUpdated !== "function") {
        throw new Error(
          "Promozione modificata, ma il riepilogo non può aggiornarsi. Ricarica la pagina."
        );
      }

      await onUpdated();

      setSelectedId("");
      setMessage(
        action === "apply"
          ? "Promozione applicata. Totale aggiornato."
          : "Promozione rimossa. Totale aggiornato."
      );
    } catch (err) {
      setError(err.message || "Impossibile aggiornare la promozione.");
    } finally {
      setBusy(false);
    }
  }

  if (!ordine) return null;

  return (
    <div className="card rounded-4 shadow-sm mb-3">
      <div className="card-body">
        <h5 className="fw-bold mb-2">Promozioni aziendali</h5>

        {activeId ? (
          <div className="alert alert-success">
            <strong>{activeTitle || `Promozione #${activeId}`}</strong>
            <div className="small mt-1">
              Stato: {activeStatus || "Attiva"}
            </div>
          </div>
        ) : (
          <p className="text-muted small">
            Nessuna promozione applicata a questo ordine.
          </p>
        )}

        {Number(ordine.b2b_discount_amount || 0) > 0 && (
          <div className="d-flex justify-content-between mb-2">
            <span>Sconto promozionale</span>
            <strong className="text-success">
              -{euro(ordine.b2b_discount_amount)}
            </strong>
          </div>
        )}

        <div className="d-flex justify-content-between mb-3">
          <strong>Totale ordine aggiornato</strong>
          <strong>{euro(ordine.total)}</strong>
        </div>

        {editable && !activeId && (
          <>
            {loading ? (
              <p className="text-muted small">Caricamento promozioni...</p>
            ) : promotions.length ? (
              <>
                <label
                  htmlFor={`b2b-promotion-${orderId}`}
                  className="form-label"
                >
                  Scegli una promozione
                </label>

                <select
                  id={`b2b-promotion-${orderId}`}
                  className="form-select mb-2"
                  value={selectedId}
                  disabled={busy}
                  onChange={(event) =>
                    setSelectedId(event.target.value)
                  }
                >
                  <option value="">Nessuna selezione</option>
                  {promotions.map((promotion) => (
                    <option key={promotion.id} value={promotion.id}>
                      {promotion.title}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="btn btn-outline-success w-100"
                  disabled={busy || !selectedId}
                  onClick={() => perform("apply", selectedId)}
                >
                  {busy ? "Attendere..." : "Applica promozione"}
                </button>
              </>
            ) : (
              <p className="text-muted small">
                Nessuna promozione applicabile disponibile.
              </p>
            )}
          </>
        )}

        {editable && activeId && (
          <button
            type="button"
            className="btn btn-outline-danger w-100"
            disabled={busy}
            onClick={() => perform("remove")}
          >
            {busy ? "Attendere..." : "Rimuovi promozione"}
          </button>
        )}

        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}

        {message && (
          <div className="alert alert-success mt-3" role="status">
            {message}
          </div>
        )}

        <p className="text-muted small mt-3 mb-0">
          L'idoneità della promozione e il totale definitivo
          vengono verificati dal server.
          Le promozioni devono essere applicate prima della firma
          e prima dell'avvio del pagamento.
        </p>
      </div>
    </div>
  );
}
